// @flow
import React, { Component, Fragment } from "react";
import { isRequired } from "../../libs/validation-rules/validation-rules";
import { formManager } from "../../libs/form-manager/form-manager";
import uuid from "uuid";

const markValidation = Symbol("@@_VALIDATE");
const handleBlur = Symbol("@@_HANDLE_BLUR");
const handleChange = Symbol("@@_HANDLE_CHANGE");


const excludedProps = ["rule", "id", "formId", "renderValidation", "isValidating"];
const defaultOnChange = () => void 0;
const filterProps = _props => ({
    ...Object.keys(_props)
        .filter(key => excludedProps.indexOf(key) === -1)
        .reduce((obj, key) => ({ ...obj, [key]: _props[key] }), {}),
    onChange: _props.onChange || ((_props.disabled || _props.readOnly) && defaultOnChange) || (() => {
    })
});


/**
 *
 * @param {*} value - value to check
 * @param {function} [callback] - callback
 * @returns {void}
 * @privates
 */
function _markValidation(value: any, callback?: any => any) {
    const _callback = callback || (() => false);
    const myRule = this.props.rule || this.defaultRule;
    const isError = !myRule(value) && this.state.renderValidation && this.state.isValidating;
    this.setState({ isError, value }, () => _callback(value));
}

/**
 *
 * @param {object} event - change event
 * @returns {void}
 */
function _handleChange(event) {
    // TODO:  move this logic to the controls. controls should return values not events. (JN)
    try {
        event.persist();
        const val = !event.dataTransfer ? event.target.value : Array.from(event.dataTransfer.files || []);
        this[markValidation](val, (() => (this.props.onChange || (() => {
        }))(val)));
    } catch { // We have a value not an event
        const val = event;
        this[markValidation](val, (() => (this.props.onChange || (() => {
        }))(val)));
    }
}


/**
 * sets the renderValidation state to true on blur
 * @returns {void}
 * @privates
 */
function _handleBlur() {
    this.setState({ renderValidation: true }, () => this[markValidation](this.state.value));
}


type Props = {
    formId: ?string,
    isValidating: ?boolean, // Master switch for form validation, true by default
    onChange: any => any,
    renderValidation: ?boolean, // Force controls that have not yet had focus to show validation message, false by default.
    rule: ?any => boolean
};


const withValidation = (WrappedComponent: Object, defaultRule: any => boolean = isRequired) => {

    class WithValidation extends Component<Props> {


        static getDerivedStateFromProps(props, prevState) {
            const { disabled, readOnly, isValidating, renderValidation, value } = props;
            const _isValidating = !readOnly && !disabled && (typeof isValidating === "boolean" ? isValidating : prevState.isValidating);
            const _renderValidation = typeof renderValidation === "boolean" ? renderValidation : prevState.renderValidation;
            const _value = typeof value === "undefined" ? prevState.value : value;
            return { isValidating: _isValidating, renderValidation: _renderValidation, value: _value };
        }

        constructor(props) {
            super(props);
            this[markValidation] = _markValidation.bind(this);
            this[handleChange] = _handleChange.bind(this);
            this[handleBlur] = _handleBlur.bind(this);
            this.formId = props.formId;
            this.controlId = props.id || uuid();
            this.defaultRule = defaultRule;
            this.state = { isValidating: true, renderValidation: false };
        }


        componentDidMount() {
            if (Boolean(this.formId)) {
                formManager.registerControl(this.formId, this);
            }
        }

        componentDidUpdate() {
            if (Boolean(this.formId)) {
                formManager.validate(this.formId);
            }
        }

        componentWillUnmount() {
            if (Boolean(this.formId)) {
                formManager.removeControl(this.formId, this);
            }
        }


        /**
         * returns if the current control value is valid
         * @returns {boolean} - is valid
         */
        validate = () => (this.props.rule || this.defaultRule)(this.state.value);

        render() {
            return (
                <Fragment>
                    <WrappedComponent
                        { ...filterProps(this.props) }
                        id={ this.controlId }
                        isError={ this.state.renderValidation && this.state.isValidating && !(this.props.rule || this.defaultRule)(this.props.value) }
                        onBlur={ this[handleBlur] }
                        onChange={ this[handleChange] }
                        value={ this.state.value }
                    />
                </Fragment>
            );
        }
    }


    return WithValidation;
};

export default withValidation;
